import {memo, useEffect, useMemo, useState} from "react";
import {
  Box,
  Combobox,
  createListCollection,
  Flex,
  HStack,
  Heading,
  Icon,
  Input,
  Stack,
  Text,
  Textarea,
  useCombobox,
  useListCollection,
} from "@chakra-ui/react";
import {
  CheckCircleIcon,
  ClockIcon,
  ChatBubbleBottomCenterIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  BellAlertIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftIcon,
} from "@heroicons/react/20/solid";
import {SelectContent, SelectItem, SelectRoot, SelectTrigger} from "../../../../../components/ui/select";
import {Button} from "../../../../../components/ui/button";
import {SegmentedControl} from "../../../../../components/ui/segmented-control";
import TagMenuButton from "../../../../../components/ui/tag-menu-button";
import {PopoverRoot, PopoverTrigger, PopoverContent, PopoverBody} from "../../../../../components/ui/popover";
import type {MockReviewStatus, ProcessHeaderProps, ProcessStatus, SignOffEntry} from "../types";
import {STATUS_LABELS} from "../types";
import {MockAvatar} from "./MockAvatar";
import {ReviewsWidget, CommentsWidget} from "./CollaborationWidget";
import {StatusButton, LockToggle, ExportButton} from "./ElcHeader";

const MOCK_REVIEWERS = ["Henry Irish", "Jenny Zhan", "James Evans", "Phoebe Scott"];

const STATUS_COLLECTION = createListCollection({
  items: [
    {value: "not-ready", label: "Not Ready"},
    {value: "in-progress", label: "In Progress"},
    {value: "in-review", label: "In Review"},
    {value: "complete", label: "Complete"},
  ] as {value: ProcessStatus; label: string}[],
});

export const StatusPicker = memo(
  ({status, onStatusChange}: {status: ProcessStatus; onStatusChange: (s: ProcessStatus) => void}) => (
    <SelectRoot
      size="sm"
      collection={STATUS_COLLECTION}
      value={[status]}
      onValueChange={details => onStatusChange(details.value[0] as ProcessStatus)}
      w="160px"
    >
      <SelectTrigger bg="white">
        <Text fontSize="sm">{STATUS_LABELS[status]}</Text>
      </SelectTrigger>
      <SelectContent>
        {STATUS_COLLECTION.items.map(item => (
          <SelectItem item={item} key={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  ),
);

export const REVIEW_STATUS_MAP: Record<
  MockReviewStatus,
  {color: string; icon: typeof ClockIcon; label: string; actionedText: string}
> = {
  pending: {color: "gray", icon: ClockIcon, label: "Awaiting review", actionedText: "requested review from"},
  approved: {color: "green", icon: CheckCircleIcon, label: "Approved", actionedText: "approved"},
  "changes-requested": {
    color: "red",
    icon: ExclamationTriangleIcon,
    label: "Changes Requested",
    actionedText: "requested changes",
  },
  commented: {color: "blue", icon: ChatBubbleBottomCenterIcon, label: "Commented", actionedText: "commented"},
};

const REVIEW_ACTIONS: {status: MockReviewStatus; icon: typeof CheckIcon; color: string; label: string}[] = [
  {status: "approved", icon: CheckIcon, color: "green", label: "Approve"},
  {status: "changes-requested", icon: ExclamationTriangleIcon, color: "red", label: "Changes"},
  {status: "commented", icon: ChatBubbleLeftIcon, color: "blue", label: "Comment"},
];

type ReviewerItem = {label: string; value: string};

export const ReviewerCombobox = memo(({onSelect}: {onSelect: (name: string) => void}) => {
  const [inputValue, setInputValue] = useState("");

  const filteredItems = useMemo<ReviewerItem[]>(() => {
    const items = inputValue.trim()
      ? MOCK_REVIEWERS.filter(name => name.toLowerCase().includes(inputValue.toLowerCase()))
      : MOCK_REVIEWERS;
    return items.map(name => ({label: name, value: name}));
  }, [inputValue]);

  const {collection, set} = useListCollection<ReviewerItem>({
    initialItems: MOCK_REVIEWERS.map(name => ({label: name, value: name})),
  });

  useEffect(() => {
    set(filteredItems);
  }, [filteredItems, set]);

  const combobox = useCombobox<ReviewerItem>({
    collection,
    inputValue,
    onInputValueChange: e => setInputValue(e.inputValue),
    onValueChange: e => {
      const selected = e.value[0];
      if (selected) onSelect(selected);
    },
    open: true,
    openOnClick: true,
    openOnKeyPress: true,
    composite: false,
  });

  return (
    <Combobox.RootProvider value={combobox}>
      <Combobox.Control>
        <Combobox.Input asChild>
          <Input size="sm" placeholder="Search team members..." autoFocus />
        </Combobox.Input>
      </Combobox.Control>
      {/* Render Content inline (no Positioner/Portal) so it works inside a Popover */}
      <Combobox.Content
        css={{
          position: "relative",
          boxShadow: "none",
          border: "none",
          bg: "transparent",
          padding: 0,
          maxH: "200px",
          overflowY: "auto",
        }}
      >
        {collection.items.map(item => (
          <Combobox.Item item={item} key={item.value}>
            <HStack gap={2}>
              <MockAvatar name={item.label} size="2xs" />
              <Text fontSize="sm">{item.label}</Text>
            </HStack>
          </Combobox.Item>
        ))}
        {collection.items.length === 0 && (
          <Box px={2} py={1.5}>
            <Text fontSize="sm" color="gray.500">
              No results
            </Text>
          </Box>
        )}
      </Combobox.Content>
    </Combobox.RootProvider>
  );
});

export const SignOffPopoverContent = memo(
  ({
    role,
    entry,
    onAssign,
    onRemove,
    onStatusChange,
    removable = true,
  }: {
    role: string;
    entry: SignOffEntry;
    onAssign: (signer: string) => void;
    onRemove: () => void;
    onStatusChange: (status: MockReviewStatus, comment?: string) => void;
    removable?: boolean;
  }) => {
    const statusInfo = entry ? REVIEW_STATUS_MAP[entry.status] : null;
    const isSelf = entry?.signer === "Henry Irish";
    const isPending = entry?.status === "pending";
    const [reviewAction, setReviewAction] = useState<MockReviewStatus>("approved");
    const [comment, setComment] = useState("");

    return (
      <>
        {/* Unassigned: pick a reviewer */}
        {!entry && (
          <Stack gap={0}>
            <Box p={3} borderBottom="1px solid {colors.gray.200}">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={0.5}>
                Request review
              </Text>
              <Text fontSize="xs" color="gray.500">
                Assign {role.toLowerCase()}
              </Text>
            </Box>
            <Box p={3}>
              <ReviewerCombobox onSelect={onAssign} />
            </Box>
          </Stack>
        )}

        {/* Self + pending: submit a review */}
        {entry && isSelf && isPending && (
          <Stack gap={0}>
            <Box p={3} borderBottom="1px solid {colors.gray.200}">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={0.5}>
                Finish your review
              </Text>
              <Text fontSize="xs" color="gray.500">
                {entry.signer} — {role}
              </Text>
            </Box>
            <Box bg="gray.50" p={3} borderBottom="1px solid {colors.gray.200}">
              <SegmentedControl
                size="sm"
                w="full"
                value={reviewAction}
                onValueChange={e => setReviewAction(e.value as MockReviewStatus)}
                items={REVIEW_ACTIONS.map(a => ({
                  value: a.status,
                  label: (
                    <HStack gap={1}>
                      <Icon asChild boxSize="1em" color={`${a.color}.600`}>
                        <a.icon />
                      </Icon>
                      <Text>{a.label}</Text>
                    </HStack>
                  ),
                }))}
              />
              <Textarea
                mt={2}
                placeholder="Leave a comment..."
                value={comment}
                onChange={e => setComment(e.target.value)}
                minH="6rem"
                fontSize="sm"
                bg="white"
              />
            </Box>
            <Box p={3}>
              <HStack justify={removable ? "space-between" : "flex-end"}>
                {removable && (
                  <Button size="xs" variant="ghost" colorPalette="red" onClick={onRemove}>
                    <Box as={TrashIcon} boxSize="14px" />
                    Remove
                  </Button>
                )}
                <Button
                  size="xs"
                  colorPalette="green"
                  disabled={reviewAction !== "approved" && !comment.trim()}
                  onClick={() => {
                    onStatusChange(reviewAction, comment.trim() || undefined);
                    setComment("");
                  }}
                >
                  Submit review
                </Button>
              </HStack>
            </Box>
          </Stack>
        )}

        {/* Other + pending: nudge / remove */}
        {entry && !isSelf && isPending && (
          <Stack gap={0}>
            <Box p={3} borderBottom="1px solid {colors.gray.200}">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={0.5}>
                {role}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {entry.signer} — Awaiting review
              </Text>
            </Box>
            <HStack px={3} py={2} justify={removable ? "space-between" : "flex-end"}>
              {removable && (
                <Button size="xs" variant="ghost" colorPalette="red" onClick={onRemove}>
                  <Box as={TrashIcon} boxSize="14px" />
                  Remove
                </Button>
              )}
              <Button size="xs" variant="outline">
                <Box as={BellAlertIcon} boxSize="14px" />
                Nudge
              </Button>
            </HStack>
          </Stack>
        )}

        {/* Reviewed: status + re-request */}
        {entry && !isPending && statusInfo && (
          <Stack gap={0}>
            <Box p={3} borderBottom="1px solid {colors.gray.200}">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={0.5}>
                {role}
              </Text>
              <HStack gap={1}>
                <Box as={statusInfo.icon} boxSize="12px" color={`${statusInfo.color}.600`} flexShrink={0} />
                <Text fontSize="xs" color="gray.500">
                  {entry.signer} — {statusInfo.label}
                </Text>
              </HStack>
            </Box>
            <HStack px={3} py={2} justify={removable ? "space-between" : "flex-end"}>
              {removable && (
                <Button size="xs" variant="ghost" colorPalette="red" onClick={onRemove}>
                  <Box as={TrashIcon} boxSize="14px" />
                  Remove
                </Button>
              )}
              <Button size="xs" variant="outline" onClick={() => onStatusChange("pending")}>
                <Box as={ArrowPathIcon} boxSize="14px" />
                Re-request
              </Button>
            </HStack>
          </Stack>
        )}
      </>
    );
  },
);

export const SignOffSlot = memo(
  ({
    role,
    entry,
    onAssign,
    onRemove,
    onStatusChange,
    removable = true,
  }: {
    role: string;
    entry: SignOffEntry;
    onAssign: (signer: string) => void;
    onRemove: () => void;
    onStatusChange: (status: MockReviewStatus, comment?: string) => void;
    removable?: boolean;
  }) => {
    const statusInfo = entry ? REVIEW_STATUS_MAP[entry.status] : null;

    return (
      <PopoverRoot lazyMount unmountOnExit positioning={{placement: "bottom-start"}}>
        <PopoverTrigger asChild>
          {entry && statusInfo ? (
            <TagMenuButton colorPalette={statusInfo.color} startElement={<statusInfo.icon />}>
              <MockAvatar name={entry.signer} size="2xs" />
            </TagMenuButton>
          ) : (
            <TagMenuButton colorPalette="gray" startElement={<PlusIcon />}>
              Request review
            </TagMenuButton>
          )}
        </PopoverTrigger>
        <PopoverContent w="353px" p={0}>
          <PopoverBody p={0}>
            <SignOffPopoverContent
              role={role}
              entry={entry}
              onAssign={onAssign}
              onRemove={onRemove}
              onStatusChange={onStatusChange}
              removable={removable}
            />
          </PopoverBody>
        </PopoverContent>
      </PopoverRoot>
    );
  },
);

export const ProcessHeader = memo(({process, ...collaborationProps}: ProcessHeaderProps) => {
  const {
    status,
    onStatusChange,
    locked,
    lockedBy,
    lockedAt,
    onLockChange,
    signOffs,
    messages,
    unreadCount,
    onOpenCollaboration,
    selection,
    onOpenReport,
  } = collaborationProps;
  return (
    <Box px={6} py={4} borderBottom="1px solid {colors.gray.200}">
      <Flex align="center" gap={2}>
        <Heading size="sm" color="gray.800" flex={1}>
          {process.name}
        </Heading>
        <ReviewsWidget signOffs={signOffs} onOpen={onOpenCollaboration} />
        <CommentsWidget messages={messages} unreadCount={unreadCount} onOpen={onOpenCollaboration} />
        <StatusButton status={status} onStatusChange={onStatusChange} />
        <LockToggle locked={locked} lockedBy={lockedBy} lockedAt={lockedAt} onToggle={onLockChange} />
        <ExportButton selection={selection} onOpenReport={onOpenReport} />
      </Flex>
    </Box>
  );
});
